/**
 * API service for configurable Work Phases.
 *
 * A work phase is the customer's own vocabulary for the kind of work a labor
 * phase represents: a Holman job order calls them "100 Installation",
 * "700 Service", "900 Inspection"; a pharma plant says granulation /
 * compression / QA; a food line says setup / production / washup.
 *
 * Until now that code lived in a labor-standard phase's free-text Notes, so it
 * could not be reported on and every PDF import had to ask the user to map
 * phase -> job role again. Work phases are per-company configuration and carry
 * a DEFAULT JOB ROLE, which is what lets an import map itself.
 */
import axios from "../utils/api-client";

const API_BASE_URL = `${process.env.REACT_APP_API_END_POINT}/WorkPhases`;

export interface WorkPhase {
	id: number;
	companyId: number;
	/** The customer's code, e.g. "100", "700", "GRAN". Shown in mono. */
	code: string;
	name: string;
	description?: string;
	/** FK to the company's Job — what an import maps a matched phase to. */
	defaultJobRoleId?: number | null;
	/** Resolved name of defaultJobRoleId, when the server projects it. */
	defaultJobRole?: string | null;
	sortOrder: number;
	isActive: boolean;
}

export interface CreateWorkPhaseDto {
	companyId: number;
	code: string;
	name: string;
	description?: string;
	defaultJobRoleId?: number | null;
	sortOrder: number;
}

export interface UpdateWorkPhaseDto extends CreateWorkPhaseDto {
	id: number;
	isActive: boolean;
}

/** The starter vocabularies the server ships. */
export type WorkPhaseTemplateKey = "vehicle-upfit" | "batch-process" | "food-line";

export interface WorkPhaseTemplatePhase {
	code: string;
	name: string;
	sortOrder: number;
	/**
	 * The job role this phase usually resolves to. It is a NAME, not an id —
	 * the client matches it against the company's real job roles.
	 */
	suggestedJobRoleName?: string;
}

export interface WorkPhaseTemplate {
	key: WorkPhaseTemplateKey;
	name: string;
	phases: WorkPhaseTemplatePhase[];
}

/** apply-template is idempotent, so the counts say what actually happened. */
export interface ApplyWorkPhaseTemplateResult {
	message?: string;
	addedCount?: number;
	skippedCount?: number;
}

export const workPhasesApi = {
	/**
	 * Work phases for a company, ordered by sortOrder then code.
	 */
	getByCompany: async (companyId: number): Promise<WorkPhase[]> => {
		const response = await axios.get<WorkPhase[]>(API_BASE_URL, {
			params: { companyId },
		});
		return response.data;
	},

	/**
	 * Get a single work phase by ID
	 */
	getById: async (id: number): Promise<WorkPhase> => {
		const response = await axios.get<WorkPhase>(`${API_BASE_URL}/${id}`);
		return response.data;
	},

	/**
	 * Create a new work phase
	 */
	create: async (phase: CreateWorkPhaseDto): Promise<WorkPhase> => {
		const response = await axios.post<WorkPhase>(API_BASE_URL, phase);
		return response.data;
	},

	/**
	 * Update an existing work phase
	 */
	update: async (id: number, phase: UpdateWorkPhaseDto): Promise<void> => {
		await axios.put(`${API_BASE_URL}/${id}`, phase);
	},

	/**
	 * Delete a work phase. The server answers 409 while a labor standard still
	 * references it — that message is meant for the user, so callers surface it
	 * rather than swallowing it.
	 */
	delete: async (id: number): Promise<void> => {
		await axios.delete(`${API_BASE_URL}/${id}`);
	},

	/**
	 * The built-in starter vocabularies (vehicle upfit, batch process, food line)
	 */
	getTemplates: async (): Promise<WorkPhaseTemplate[]> => {
		const response = await axios.get<WorkPhaseTemplate[]>(`${API_BASE_URL}/templates`);
		return response.data;
	},

	/**
	 * Apply a starter set to a company. Idempotent — codes the company already
	 * has are left untouched.
	 */
	applyTemplate: async (
		companyId: number,
		key: WorkPhaseTemplateKey
	): Promise<ApplyWorkPhaseTemplateResult> => {
		const response = await axios.post<ApplyWorkPhaseTemplateResult>(
			`${API_BASE_URL}/apply-template`,
			{ companyId, key }
		);
		return response.data;
	},
};

export default workPhasesApi;
